import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import dayjs from 'dayjs';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import { DocumentType, EntityType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileManagerComponent, FileViewerComponent],
  template: `
    <div class="w-screen max-w-md">
      <aux-file-manager
        #fileManager
        class="h-32"
        [fetchFilesOnInit]="true"
        [pathFn]="pathFn()"
        [eager]="false"
        [metadata]="metadata"
        [insertDocument]="true"
        [document_entity_id]="entity_id"
      />
      <div class="mt-4 max-h-60 overflow-auto">
        <aux-file-viewer
          [fileManager]="fileManager"
          [onlyShowUploaded]="false"
          [removeOnlyFromStore]="true"
          [showUserAndDate]="true"
          (removeFileChange)="removeFileChange($event)"
        />
      </div>
    </div>
  `,
})
export class SupportModalComponent {
  ref = inject(
    CustomOverlayRef<unknown, { current_month: string; entity_id: string; vendor_id: string }>
  );

  private apiService = inject(ApiService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  entity_id = this.ref.data?.entity_id;
  @ViewChild('fileManager') fileManager?: FileManagerComponent;
  metadata: FileMetadata = { documentType: DocumentType.DOCUMENT_CLOSE_SUMMARY };

  private removedFiles: File[] = [];

  private convertDateToMonthlyReportLabel(current_period: string, current_format: string) {
    const temp_date = dayjs(current_period, current_format, true);

    return temp_date.format('MMM-YYYY');
  }

  async onSaveDocuments() {
    const trialId = this.mainQuery.getValue().trialKey;

    if (this.fileManager && this.ref.data) {
      const files = this.fileManager.fileQuery.getAll();

      await this.fileManager?.fileService.removeFiles(this.removedFiles);

      files.forEach((file) => {
        this.fileManager?.fileStore.update(file.id, { ...file, bucket_key: file.bucket_key });
      });

      const uploadedFiles = this.fileManager.fileQuery.getAll({
        filterBy: (entity) => !entity.uploaded && !!entity.rawFile,
      });

      const zip_period_label = this.convertDateToMonthlyReportLabel(
        this.ref.data.current_month,
        'YYYY-MM-DD'
      );

      //remove tags to removed files from monthly reports zip tags
      this.removedFiles.forEach((file) => {
        void this.apiService.removeFileFromMonthlyReportsList(
          trialId,
          zip_period_label,
          file.bucket_key
        );
      });

      await this.fileManager.fileService.uploadFiles({
        filterLocalFilesByUploadedFlag: true,
        include_for_audit_package: true,
        insertDocument: true,
        is_metadata_editable: false,
        metadata: {
          documentType: DocumentType.DOCUMENT_MANUAL_ADJUSTMENT,
          entity_id: this.ref.data.entity_id,
          entity_type_id: EntityType.ACTIVITY,
          target_date: this.ref.data.current_month,
          vendor: this.ref.data.vendor_id,
        },
        overrideFilePath: undefined,
        removeFilesOnPartialFailure: false,
        showSuccess: false,
      });
      const amountUpdatedDocuments = uploadedFiles.length + this.removedFiles.length;

      this.overlayService.success(
        `${amountUpdatedDocuments} file${
          amountUpdatedDocuments > 1 ? 's' : ''
        } updated successfully!`
      );

      this.ref.close({
        total_documents: this.fileManager?.fileQuery.getAll().length || 0,
      });
    }
  }

  pathFn = () => {
    const trialId = this.mainQuery.getValue().trialKey;

    return () => `trials/${trialId}/month-close-manual-adjustment/${this.ref.data?.current_month}/`;
  };

  removeFileChange(file: File) {
    this.removedFiles.push(file);
  }
}
